import { User, LoginCredentials, RegisterData, AuthResponse } from '../types/auth';
import API_CONFIG, { buildApiUrl } from '../config/api';
import { TylerState, updateReact } from '../utils/TylerState';


class AuthService {
  private apiUrl: string = API_CONFIG.BASE_URL;
  private googleClientId: string = process.env.REACT_APP_GOOGLE_CLIENT_ID || '113175683643-d58rfub8jjnfpdhi9vramnmpk1p39jdt.apps.googleusercontent.com';

  constructor() {
    // Check if user is authenticated on startup
    this.checkAuthStatus();
  }

  // Get token from localStorage
  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  // Set token in localStorage
  private setToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  // Remove token from localStorage
  private removeToken(): void {
    localStorage.removeItem('auth_token');
  }

  // Get user from localStorage
  private getStoredUser(): User | null {
    const userStr = localStorage.getItem('auth_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  // Set user in localStorage
  private setStoredUser(user: User): void {
    localStorage.setItem('auth_user', JSON.stringify(user));
  }

  // Remove user from localStorage
  private removeStoredUser(): void {
    localStorage.removeItem('auth_user');
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${this.apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }

    const data = await response.json();

    if (!data.token || !data.user) {
      throw new Error('Invalid response from server');
    }

    // Store token and user in localStorage
    this.setToken(data.token);
    this.setStoredUser(data.user);

    // Update global state
    TylerState.user = data.user;
    updateReact();

    return { user: data.user };
  }

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await fetch(`${this.apiUrl}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: userData.email,
        username: userData.email.split('@')[0],
        password: userData.password
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Registration failed');
    }

    const data = await response.json();

    if (!data.token || !data.user) {
      throw new Error('Invalid response from server');
    }

    // Store token and user in localStorage
    this.setToken(data.token);
    this.setStoredUser(data.user);

    // Update global state
    TylerState.user = data.user;
    updateReact();

    return { user: data.user };
  }

  async getCurrentUser(): Promise<User | null> {
    return this.getStoredUser();
  }

  async verifyToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${this.apiUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        this.logout();
        return false;
      }

      return response.ok;
    } catch (error) {
      console.log('Token verification failed:', error);
      return false;
    }
  }

  async logout(): Promise<void> {
    // Clear token and user from localStorage
    this.removeToken();
    this.removeStoredUser();

    // Clear global state
    TylerState.user = null;
    updateReact();
  }

  async checkAuthStatus(): Promise<boolean> {
    const token = this.getToken();
    const user = this.getStoredUser();

    if (!token || !user) {
      return false;
    }

    try {
      const response = await fetch(`${this.apiUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update user data from backend
        this.setStoredUser(data);
        TylerState.user = data;
        updateReact();
        return true;
      }

      // Token invalid, clear auth
      this.logout();
      return false;
    } catch (error) {
      console.log('Auth check failed:', error);
      return false;
    }
  }

  getUser(): User | null {
    return this.getStoredUser();
  }

  isAuthenticated(): boolean {
    return this.getToken() !== null && this.getStoredUser() !== null;
  }

  clearInconsistentState(): void {
    this.logout();
  }

  async refreshUserData(): Promise<User | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${this.apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        this.setStoredUser(userData);
        TylerState.user = userData;
        updateReact();
        return userData;
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }

    return null;
  }

  async refreshToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${this.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Update token and user
        this.setToken(data.token);
        this.setStoredUser(data.user);
        TylerState.user = data.user;
        updateReact();

        return true;
      }

      // Token invalid, logout
      this.logout();
      return false;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }

  // Removed automatic token refresh - tokens last 1 year, no need for auto-refresh
  // If needed, can manually call refreshToken() or user can re-login

  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Email verification methods
  async verifyEmail(token: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        // Refresh user data to get updated verification status
        await this.refreshUserData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Email verification failed:', error);
      return false;
    }
  }

  async resendVerification(email: string): Promise<void> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to resend verification email');
      }
    } catch (error) {
      console.error('Failed to resend verification:', error);
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send reset email');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token: token,
        password: newPassword
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reset password');
    }
  }

  // Google OAuth methods
  googleLogin(): void {
    //window.console.log('🚀 FORCED LOG: googleLogin() called');
    //window.console.log('🚀 FORCED LOG: Current window.location.origin:', window.location.origin);
    //window.console.log('🚀 FORCED LOG: Google Client ID:', this.googleClientId);
    
    try {
      const googleAuthUrl = this.buildGoogleAuthUrl();
      //window.console.log('🚀 FORCED LOG: Built Google Auth URL:', googleAuthUrl);
      //window.console.log('🚀 FORCED LOG: About to redirect to Google OAuth');
      window.location.href = googleAuthUrl;
      // Do not return a rejecting promise; we're navigating away
      return;
    } catch (err) {
      // Swallow to avoid unhandled rejection overlay in dev
      window.console.error('❌ FORCED LOG: Google login init failed:', err);
      return;
    }
  }

  private buildGoogleAuthUrl(): string {
    //window.console.log('🚀 FORCED LOG: buildGoogleAuthUrl() called');
    //window.console.log('🚀 FORCED LOG: Building URL with origin:', window.location.origin);
    
    const params = new URLSearchParams({
      client_id: this.googleClientId,
      redirect_uri: `${window.location.origin}/auth/google/callback`,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'select_account'
    });
    
    const finalUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    //window.console.log('🚀 FORCED LOG: Final Google Auth URL:', finalUrl);
    
    return finalUrl;
  }

  async handleGoogleCallback(code: string): Promise<AuthResponse> {
    const response = await fetch(`${this.apiUrl}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        redirect_uri: `${window.location.origin}/auth/google/callback`
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Google authentication failed');
    }

    const data = await response.json();

    if (!data.token || !data.user) {
      throw new Error('Invalid response from server');
    }

    // Store token and user in localStorage
    this.setToken(data.token);
    this.setStoredUser(data.user);

    // Update global state
    TylerState.user = data.user;
    updateReact();

    return { user: data.user };
  }

  private async initializeGoogleAuth(): Promise<any> {
    //window.console.log('🚀 FORCED LOG: initializeGoogleAuth called');
    //window.console.log('🚀 FORCED LOG: Google Client ID:', this.googleClientId);
    
    return new Promise((resolve, reject) => {
      // Load Google OAuth script if not already loaded
      if (window.gapi) {
        //window.console.log('🚀 FORCED LOG: Google API already loaded, initializing auth2');
        window.gapi.load('auth2', () => {
          //window.console.log('🚀 FORCED LOG: Google auth2 loaded, initializing');
          window.gapi.auth2.init({
            client_id: this.googleClientId,
            scope: 'email profile'
          }).then((auth2: any) => {
            //window.console.log('✅ FORCED LOG: Google auth2 initialized successfully');
            resolve(auth2);
          }).catch((error: any) => {
            window.console.error('❌ FORCED LOG: Google auth2 init failed:', error);
            reject(error);
          });
        });
      } else {
        //window.console.log('🚀 FORCED LOG: Loading Google API script');
        // Load Google OAuth script
        const script = document.createElement('script');
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => {
          //window.console.log('✅ FORCED LOG: Google API script loaded');
          window.gapi.load('auth2', () => {
            //window.console.log('🚀 FORCED LOG: Google auth2 loaded, initializing');
            window.gapi.auth2.init({
              client_id: this.googleClientId,
              scope: 'email profile'
            }).then((auth2: any) => {
              //window.console.log('✅ FORCED LOG: Google auth2 initialized successfully');
              resolve(auth2);
            }).catch((error: any) => {
              window.console.error('❌ FORCED LOG: Google auth2 init failed:', error);
              reject(error);
            });
          });
        };
        script.onerror = (error: any) => {
          window.console.error('❌ FORCED LOG: Google API script failed to load:', error);
          reject(error);
        };
        document.head.appendChild(script);
      }
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${this.apiUrl}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to change password');
    }
  }

  async deleteAccount(password: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${this.apiUrl}/auth/delete-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete account');
    }

    this.logout();
  }
}

// Add Google OAuth types to window object
declare global {
  interface Window {
    gapi: any;
  }
}

// Create a named instance
const authServiceInstance = new AuthService();

// Export the named instance as default
export default authServiceInstance; 